import { useState, useEffect, useCallback } from "react";
import {
  searchSingleModule,
  searchSelectedModules,
} from "services/globalSearch";
import {
  CATEGORIES,
  getPermittedModuleKeys,
} from "components/shared/global-search/searchConfig";
import useDebouncedValue from "./useDebouncedValue";

const STORAGE_KEY_ACTIVE_CATEGORIES = "globalSearchActiveCategories";
const STORAGE_KEY_CATEGORY_COUNTS = "globalSearchCategoryCounts";

const VALID_CATEGORIES = new Set(Object.values(CATEGORIES));
const MODULE_KEYS_ONLY = new Set(
  Object.values(CATEGORIES).filter((k) => k !== CATEGORIES.ALL)
);

const getDefaultCategories = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ACTIVE_CATEGORIES);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    // Only keep keys that are still valid module keys AND the user still has permission for
    const permittedKeySet = new Set(getPermittedModuleKeys());
    const valid = parsed.filter(
      (k) => MODULE_KEYS_ONLY.has(k) && permittedKeySet.has(k)
    );
    return valid;
  } catch {
    return [];
  }
};

const persistActiveCategories = (categories) => {
  try {
    const arr = Array.isArray(categories) ? categories : [];
    localStorage.setItem(STORAGE_KEY_ACTIVE_CATEGORIES, JSON.stringify(arr));
    arr.forEach((key) => {
      const json = localStorage.getItem(STORAGE_KEY_CATEGORY_COUNTS);
      const counts = json ? JSON.parse(json) : {};
      counts[key] = (counts[key] || 0) + 1;
      localStorage.setItem(STORAGE_KEY_CATEGORY_COUNTS, JSON.stringify(counts));
    });
  } catch {
    // ignore storage errors
  }
};

/**
 * Custom hook for global search functionality
 * Manages search state, debouncing, API calls, and multi-select category state.
 */
const useGlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategories, setActiveCategories] = useState(() =>
    getDefaultCategories()
  );
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Toggle category: "all" clears selection; otherwise add/remove from selection
  const handleCategoryChange = useCallback((category) => {
    if (!category || !VALID_CATEGORIES.has(category)) return;

    if (category === CATEGORIES.ALL) {
      setActiveCategories([]);
      persistActiveCategories([]);
      return;
    }

    // Prevent selecting a category the user has no permission for
    const permittedKeySet = new Set(getPermittedModuleKeys());
    if (!permittedKeySet.has(category)) return;

    setActiveCategories((prev) => {
      const next = prev.includes(category)
        ? prev.filter((k) => k !== category)
        : [...prev, category];
      persistActiveCategories(next);
      return next;
    });
  }, []);

  // Clear results immediately when query is emptied (before debounce)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults({});
      setError(null);
    }
  }, [searchQuery]);

  // Perform search with race condition handling
  useEffect(() => {
    const controller = new AbortController();

    const performSearch = async () => {
      if (!debouncedQuery.trim()) {
        setResults({});
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let searchResults;

        if (activeCategories.length === 0) {
          // "All" mode — only search modules the user is permitted to access
          const permittedKeysList = getPermittedModuleKeys();
          searchResults = await searchSelectedModules(
            permittedKeysList,
            debouncedQuery,
            controller.signal
          );
        } else if (activeCategories.length === 1) {
          const moduleResults = await searchSingleModule(
            activeCategories[0],
            debouncedQuery,
            controller.signal
          );
          searchResults = { [activeCategories[0]]: moduleResults };
        } else {
          searchResults = await searchSelectedModules(
            activeCategories,
            debouncedQuery
          );
        }

        // Only update state if request wasn't aborted
        if (!controller.signal.aborted) {
          setResults(searchResults);
        }
      } catch (err) {
        // Ignore abort errors, handle others
        if (err.name !== "AbortError" && !controller.signal.aborted) {
          console.error("Error in global search:", err);
          setError(err.message || "An error occurred during search");
          setResults({});
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    performSearch();

    // Cleanup: abort pending request on unmount or dependency change
    return () => controller.abort();
  }, [debouncedQuery, activeCategories]);

  // Clear all search state
  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setResults({});
    setError(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    activeCategories,
    setActiveCategory: handleCategoryChange,
    results,
    loading,
    error,
    clearSearch,
  };
};

export default useGlobalSearch;
